import React, { useContext  } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import { createEvent, editEvent } from "app/actions/events";
import { UserContext, RoleType } from "app/contexts/UserContext";

import styles from "./styles.module.scss";

type PropsDef = {
  isOpen: boolean;
  title: string;
  children: React.ReactNode;
  status: "success" | "error" | "conflict";
  onDismiss?: () => void;
  onConfirm?: () => void;
  payload?: any;
  action?: "create" | "edit";
  ownEvents?: boolean;
  paymentMethod?: "online" | "package" | string;
  // False for a conflict nobody can book over, such as a bay another lesson holds.
  canOverride?: boolean;
};

export default function InfoModal(props: PropsDef) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userRole = useContext(UserContext);

  const {
    isOpen,
    title,
    children,
    status,
    onDismiss,
    payload,
    action,
    ownEvents = false,
    paymentMethod,
    canOverride = true,
  } = props;

  if (!isOpen) {
    return null;
  }

  const isFinancial = paymentMethod === "online" || paymentMethod === "package";

  const icon = isFinancial
    ? `ico ico-dollar ${styles.dollar_icon}`
    : status === "success"
      ? `ico ico-check ${styles.success}`
      : `ico ico-x ${styles.error}`;

  const onConfirmConflict = () => {
    const actionFn = action === "create" ? createEvent : editEvent;

    onDismiss();
    dispatch(actionFn({ ...payload, ignore_conflict: true }));
  };

  const closeButton = (
    <div className={styles.close_button} onClick={onDismiss}>
      <i className="ico ico-x" />
    </div>
  );

  const allCoachIds: string[] = payload?.coachProfileIds || [payload?.coachProfileId];
  const isCoach = userRole.role === RoleType.COACH;
  const ownEvent = ownEvents || (isCoach && allCoachIds.includes(userRole.id));
  const allowConfirmOnConflict = canOverride && (userRole.behavesLikeAdmin || ownEvent);

  const showConflictConfirmation = status === 'conflict' && allowConfirmOnConflict;
  const showOk = status !== 'conflict' || !allowConfirmOnConflict;

  let headerTitle = title;

  // A conflict that the caller offers to override has a title that asks for a
  // confirmation. A user who cannot confirm it gets the plain title instead.
  // A conflict with no override (a taken bay) keeps its own title.
  if (status === 'conflict' && canOverride && !showConflictConfirmation) {
    headerTitle = t('Scheduling Conflict');
  }

  return (
    <ModalLayout
      isOpen={isOpen}
      classNames={`${styles.modal} info-modal-container`}
      data-testid={`calendar-info-modal-${status}`}
    >
      <div className={`${styles.modal_header} ${styles.w_100} info-modal-header`}>
        <div className={styles.w_100}>
          {status === "conflict" && closeButton}
          <i className={`${icon} ${styles.modal_status_icon}`} />
        </div>
        <h2>{headerTitle}</h2>
      </div>
      <div className={`${styles.modal_content} info-modal-content`}>
        {children}
      </div>

      {showOk && (
        <button
          onClick={onDismiss}
          // After a conflict with no override, the event form stays open with
          // its values, so the user can pick another bay or time. The
          // calendars keep the time selection (and so the form) on a click in
          // `.confirm_cancel_conflict` (their `unselectCancel` list).
          className={`${styles.confirm_button} btn btn-primary ${status === "conflict" ? "confirm_cancel_conflict" : ""}`}
          data-testid="calendar-info-modal-ok-button"
        >
          {t("Ok")}
        </button>
      )}

      {showConflictConfirmation && (
        <div className={styles.confirm_conflict_actions}>
          <button
            onClick={onConfirmConflict}
            className={`${styles.confirm_button} btn btn-primary`}
            data-testid="calendar-info-modal-confirm-button"
          >
            {t("Confirm")}
          </button>
          <button
            onClick={onDismiss}
            className={`${styles.cancel_button} btn confirm_cancel_conflict`}
            data-testid="calendar-info-modal-cancel-button"
          >
            {t("Cancel")}
          </button>
        </div>
      )}
    </ModalLayout>
  );
}
